import React, { useState } from 'react';
import { getDeepInsights } from '../../services/deepInsights';
import renderPatternDetection from './renderPatternDetection.js';
import renderConversationalCoach from './renderConversationalCoach.js'
import renderSuggestions from './renderSuggestions.js';
import renderLearningPlan from './renderLearningPlan.js';
import renderEnhancedAnalysis from './renderEnhancedAnalysis.js'

const AdvancedAIInterface = ({ userId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);

  const predefinedQueries = {
    patterns: [
      "What patterns do you see in my trading behavior?",
      "Do I over-leverage after losing trades?",
      "Do I trade differently on certain days of the week?",
      "What are my common trading mistakes?"
    ],
    coaching: [
      "Review my last 5 losing trades - what went wrong?",
      "How can I improve my risk management?",
      "Why do I keep making the same mistakes?",
      "Help me understand my emotional trading patterns"
    ],
    suggestions: [
      "What should I focus on to improve my trading?",
      "Give me personalized recommendations",
      "How can I optimize my trading schedule?",
      "What specific areas need work?"
    ],
    plans: [
      "Create a learning plan for this week",
      "What should my trading focus be this month?",
      "Help me set improvement goals",
      "Design a practice routine for me"
    ]
  };

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    console.log ("this is deepinsights")
    try {
      // Use the new deep insights service
      const data = await getDeepInsights(searchQuery, conversationHistory);

      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      // Parse structured data from the answer if backend returned raw JSON in the answer field
      let parsedFromAnswer = {};
      if (data.answer && !data.plan && !data.suggestions && !data.patterns) {
        try {
          // Strip markdown code fences and extract JSON
          let cleaned = data.answer.trim();
          cleaned = cleaned.replace(/^```(?:json)?\s*\n?/i, '');
          cleaned = cleaned.replace(/\n?```[\s\S]*$/i, '');
          const jsonStart = cleaned.indexOf('{');
          const jsonEnd = cleaned.lastIndexOf('}');
          if (jsonStart >= 0 && jsonEnd > jsonStart) {
            const inner = JSON.parse(cleaned.substring(jsonStart, jsonEnd + 1));
            parsedFromAnswer = {
              analysis: inner.analysis || inner.summary || '',
              plan: inner.plan || null,
              suggestions: inner.suggestions || null,
              patterns: inner.patterns || null,
              insights: inner.insights || null,
              observations: inner.observations || null,
              follow_up_suggestions: inner.followUpSuggestions || data.follow_up_suggestions
            };
          }
        } catch (e) {
          console.warn('[SearchInterface] Could not parse JSON from answer field:', e);
        }
      }

      // Map response to expected format for backward compatibility
      const mappedResults = {
        ...data,
        ...parsedFromAnswer,
        query: searchQuery,
        // Map 'answer' to 'response' for coaching type
        response: parsedFromAnswer.analysis || data.answer,
        // Use parsed analysis if available, otherwise fall back to answer
        analysis: parsedFromAnswer.analysis || data.answer
      };

      setResults(mappedResults);
      setQuery(searchQuery);

      // If it's a reflective coaching response, add to conversation history
      if (data.type === 'reflective_coaching') {
        setConversationHistory(prev => [
          ...prev,
          { role: 'user', content: searchQuery },
          { role: 'assistant', content: data.answer }
        ]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message || 'Analysis failed. Please try again.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = (followUpQuery) => {
    setQuery(followUpQuery);
    handleSearch(followUpQuery);
  };

 

  return (
    <div className="advanced-ai-interface p-4 sm:p-6 lg:p-8">
      <h2 className="text-3xl font-bold mb-6 text-foreground">Sage</h2>

      {/* Search Input */}
      <div className="search-input mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask about patterns, get coaching, or request personalized suggestions..."
            className="flex-1 p-4 border border-border bg-input text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-lg placeholder:text-muted-foreground"
            onKeyPress={(e) => e.key === 'Enter' && !loading && handleSearch()}
            disabled={loading}
          />
          <button
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading text-center py-12">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-4"></div>
            <span className="text-muted-foreground text-lg">
              Analyzing your trading patterns...
            </span>
          </div>
        </div>
      )}

      {/* Predefined Query Categories */}
      {!loading && <div className="predefined-queries mb-8">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Choose an analysis type:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(predefinedQueries).map(([category, queries]) => (
            <div key={category} className="category-group">
              <h4 className="font-semibold text-foreground mb-2 capitalize flex items-center">
                {category === 'patterns' && '🔍 '}
                {category === 'coaching' && '💬 '}
                {category === 'suggestions' && '💡 '}
                {category === 'plans' && '📚 '}
                {category}
              </h4>
              <div className="space-y-2">
                {queries.map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleSearch(query)}
                    disabled={loading}
                    className="w-full text-left px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors disabled:opacity-50 border border-border text-foreground"
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>}

      {/* Error Display */}
      {error && (
        <div className="error-message mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
          <p className="text-destructive">❌ {error}</p>
        </div>
      )}

      {/* Results */}
        {results && results.disclaimer && (
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground italic">{results.disclaimer}</p>
            </div>
          )}
      {results && (
        <div className="results-container">
          {results.type === 'pattern_observation' && renderPatternDetection(results)}
          {results.type === 'reflective_coaching' && renderConversationalCoach(results)}
          {results.type === 'behavioral_observations' && renderSuggestions(results)}
          {results.type === 'self_reflection_plan' && renderLearningPlan(results)}
          {(results.type === 'time_analysis' || results.type === 'risk_awareness' ||
            results.type === 'performance_review' || results.type === 'search') && renderEnhancedAnalysis(results)}

        
        </div>
      )}

    </div>
  );
};

export default AdvancedAIInterface;
